import React from 'react';
import {
  FeatureProvider,
  FEATURE_DEFAULTS,
  mergeFeatureFlags,
  closeHardGates,
} from '@caro/shared/features';
import type { FeatureFlags } from '@caro/shared/features';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';

/**
 * Seeds the shared `<FeatureProvider>` for the resident-app (R2 / T034).
 *
 * Spec: specs/20260602-resident-device-autoprovision/plan.md §5 R2
 *
 * Reads the building's merged effective feature flags from the
 * `getBuildingConfig` response (`building.features` — the server already
 * resolves code defaults ⊕ the whole ancestor chain via `resolveFeatureChain`
 * and forces `emergency` on).
 *
 * ## Seeding before the response arrives
 *
 * While the config is loading (or when unauthenticated / no tenant) we seed
 * with the code defaults, so the UI never gates off a feature spuriously — with
 * one exception. `HARD_GATED_FEATURES` (`openDoor`) are seeded **off** via
 * `closeHardGates`: they guard a physical action, and their code default is
 * `true`, so seeding them optimistically would render a tappable door-release
 * button on a site whose operator has forbidden it, for the whole duration of
 * the fetch. Fail-closed is the only safe direction there. `emergency` is
 * always on regardless.
 *
 * This is defence in depth, not the control: `POST /api/v1/door/open`
 * independently re-resolves the chain and 403s when the feature is off.
 */
interface BuildingConfigResponse {
  building?: { features?: Partial<FeatureFlags> };
}

export function BuildingFeaturesProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { data } = useApi<BuildingConfigResponse>(
    user?.tenantId ? `/api/v1/building/${user.tenantId}/config` : null,
  );

  // The server returns already-resolved flags; re-merging over the code
  // defaults is idempotent and guarantees a complete map (+ emergency on) even
  // if the response is partial. Until it arrives, hard-gated features stay off.
  const resolved = data?.building?.features;
  const features = resolved
    ? mergeFeatureFlags(FEATURE_DEFAULTS, resolved)
    : closeHardGates(FEATURE_DEFAULTS);

  return <FeatureProvider features={features}>{children}</FeatureProvider>;
}
